import Button, { IconButton } from '@components/shared/Button'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { useState, CSSProperties } from 'react'
import {
  SWAP_ANNOUNCEMENT,
  SWAP_ANNOUNCEMENT_ID_KEY,
} from 'utils/announcements'
import Modal from '@components/shared/Modal'
import RenderMarkdown from './RenderMarkdown'
import { useTranslation } from 'next-i18next'
import clsx from 'clsx'

type TFn = ReturnType<typeof useTranslation>['t']

const PILL_BASE =
  'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] leading-tight'

const SwapAnnouncementBody = ({
  titleKey,
  t,
  showLearnMore,
}: {
  titleKey: string
  t: TFn
  showLearnMore: boolean
}) => (
  <div className="space-y-2.5">
    <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF6B35]">
      ride.markets
    </div>
    <h4 className="leading-tight text-th-fgd-1">{t(titleKey)}</h4>
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={clsx(
          PILL_BASE,
          'border-[#FF6B35]/40 bg-[#FF6B35]/10 text-[#FF6B35]',
        )}
      >
        {t('swap.pills.leverage')}
      </span>
      <span
        className={clsx(
          PILL_BASE,
          'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
        )}
      >
        {t('swap.pills.noLiquidation')}
      </span>
      <span
        className={clsx(
          PILL_BASE,
          'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
        )}
      >
        {t('swap.pills.noFunding')}
      </span>
    </div>
    {showLearnMore ? (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.1em] text-th-fgd-2 group-hover:text-[#FF6B35]">
        {t('learn-more')} <span aria-hidden="true">→</span>
      </span>
    ) : null}
  </div>
)

const SwapAnnouncement = () => {
  const { t } = useTranslation('announcements')
  const [announcementClosedId, setAnnouncementClosedId] = useLocalStorage(
    SWAP_ANNOUNCEMENT_ID_KEY,
  )
  const [showLongDescription, setShowLongDescription] = useState(false)
  const {
    id,
    titleKey,
    descriptionKey,
    show,
    longDescriptionKey,
    imagePath,
    linkPath,
    external,
  } = SWAP_ANNOUNCEMENT

  return (
    <>
      {show && announcementClosedId !== id ? (
        <div
          className={clsx(
            'group relative mb-4 w-full max-w-lg overflow-hidden rounded-lg border border-[#FF6B35]/20 bg-[#0e0a07]',
            !!longDescriptionKey &&
              'default-transition md:hover:border-[#FF6B35]/40',
            imagePath &&
              'before:absolute before:inset-0 before:bg-[image:var(--bg-image)] before:bg-cover before:bg-center before:opacity-25 before:content-[""]',
          )}
          style={
            imagePath
              ? ({ '--bg-image': `url(${imagePath})` } as CSSProperties)
              : undefined
          }
        >
          {!imagePath ? (
            <div
              className="pointer-events-none absolute inset-0 overflow-hidden"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,107,53,0.22),transparent_60%)]" />
              <svg
                viewBox="0 0 240 80"
                preserveAspectRatio="none"
                className="absolute bottom-0 right-0 h-full w-2/3 opacity-90"
              >
                <defs>
                  <linearGradient id="ride-line" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FF6B35" stopOpacity="0" />
                    <stop offset="100%" stopColor="#FF6B35" stopOpacity="1" />
                  </linearGradient>
                  <linearGradient id="ride-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B35" stopOpacity="0.32" />
                    <stop offset="100%" stopColor="#FF6B35" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,60 L24,55 L48,58 L72,46 L96,50 L120,34 L144,40 L168,24 L192,20 L216,12 L240,4 L240,80 L0,80 Z"
                  fill="url(#ride-fill)"
                />
                <path
                  d="M0,60 L24,55 L48,58 L72,46 L96,50 L120,34 L144,40 L168,24 L192,20 L216,12 L240,4"
                  stroke="url(#ride-line)"
                  strokeWidth="1.8"
                  vectorEffect="non-scaling-stroke"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="absolute right-3 top-3 size-2 animate-pulse rounded-full bg-[#FF6B35] shadow-[0_0_12px_3px_rgba(255,107,53,0.55)]" />
            </div>
          ) : null}
          {longDescriptionKey ? (
            <button
              onClick={() => setShowLongDescription(!showLongDescription)}
              className="default-transition relative block w-full p-3.5 text-left"
            >
              <SwapAnnouncementBody
                titleKey={titleKey}
                t={t}
                showLearnMore
              />
            </button>
          ) : (
            <div className="relative block p-3.5">
              <SwapAnnouncementBody
                titleKey={titleKey}
                t={t}
                showLearnMore={false}
              />
            </div>
          )}
          <IconButton
            onClick={() => setAnnouncementClosedId(id)}
            hideBg
            className="absolute right-2 top-2 shrink-0 text-th-fgd-2"
          >
            <XMarkIcon className="size-5" />
          </IconButton>
        </div>
      ) : null}

      {showLongDescription && longDescriptionKey ? (
        <Modal
          isOpen={showLongDescription}
          onClose={() => setShowLongDescription(false)}
        >
          <RenderMarkdown markdown={t(longDescriptionKey)} />
          <div className="mt-6 flex justify-center">
            {linkPath ? (
              <a
                href={linkPath}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                className="w-full"
                onClick={() => setShowLongDescription(false)}
              >
                <Button className="w-full">{t('lets-go')}</Button>
              </a>
            ) : (
              <Button
                className="w-full"
                onClick={() => setShowLongDescription(false)}
              >
                {t('lets-go')}
              </Button>
            )}
          </div>
        </Modal>
      ) : null}
    </>
  )
}

export default SwapAnnouncement
